import { useEffect, useRef, useCallback, useState } from 'react';

import { getBrowserLocation, getWebSocketConstructor, type WebSocketLike } from './browser';

export type DeviceEventType =
  | 'device_registered'
  | 'device_unregistered'
  | 'device_state_changed'
  | 'device_availability_changed';

export interface DeviceState {
  switch?: { on?: boolean };
  light?: {
    on?: boolean;
    brightness?: number;
    color?: { red: number; green: number; blue: number };
  };
  windowCovering?: { closedPercentage?: number };
  fan?: { on?: boolean; intensity?: number; swivel?: boolean };
  speaker?: { on?: boolean; volume?: number; playState?: string; source?: string };
  tv?: { on?: boolean; volume?: number; source?: string; muted?: boolean; playState?: string };
}

export interface DeviceStreamEvent {
  type: DeviceEventType;
  deviceId: string;
  humanId?: string;
  name?: string;
  deviceType?: string;
  deviceSubtype?: string;
  provider?: string;
  availability?: string;
  state?: DeviceState;
  oldState?: DeviceState;
  timestamp: string;
}

export interface UseDeviceStreamOptions {
  /**
   * Filter pattern for device ID/humanID (optional, omit for all devices).
   * Supports:
   * - Single device: "shelly-living-room"
   * - Multiple devices: "shelly-living-room,hue-bedroom"
   * - Wildcards: "hue-*", "*-living-room", "*kitchen*"
   */
  filter?: string;
  /**
   * Filter by device type (optional).
   * Supports comma-separated values: "light", "switch,light", "tv,speaker"
   */
  type?: string;
  /** Whether to connect automatically (default: true) */
  enabled?: boolean;
  /** Callback when a device event is received */
  onEvent?: (event: DeviceStreamEvent) => void;
  /** Reconnection delay in ms (default: 3000) */
  reconnectDelay?: number;
}

export interface UseDeviceStreamResult {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Most recent event received */
  lastEvent: DeviceStreamEvent | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

/**
 * Hook to subscribe to real-time device updates via WebSocket
 *
 * @param options - Configuration options
 * @returns Connection state and controls
 */
export function useDeviceStream(options: UseDeviceStreamOptions = {}): UseDeviceStreamResult {
  const { filter, type, enabled = true, onEvent, reconnectDelay = 3000 } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<DeviceStreamEvent | null>(null);

  const wsRef = useRef<WebSocketLike | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const onEventRef = useRef(onEvent);

  // Keep callback ref updated
  onEventRef.current = onEvent;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Build WebSocket URL
    const location = getBrowserLocation();
    const WebSocketImpl = getWebSocketConstructor();
    if (!location || !WebSocketImpl) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = location.host;
    const params = new URLSearchParams();
    if (filter) params.set('filter', filter);
    if (type) params.set('type', type);
    const queryString = params.toString();
    const url = `${protocol}//${host}/api/devices/stream${queryString ? `?${queryString}` : ''}`;

    const ws = new WebSocketImpl(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) {
        setIsConnected(true);
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setIsConnected(false);
        // Schedule reconnection
        if (enabled) {
          clearReconnectTimeout();
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      }
    };

    ws.onerror = () => {
      // Error will be followed by close, so we don't need to do much here
      ws.close();
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      try {
        const data = JSON.parse(event.data) as DeviceStreamEvent;
        setLastEvent(data);
        onEventRef.current?.(data);
      } catch {
        // Ignore malformed messages
      }
    };
  }, [enabled, filter, type, reconnectDelay, clearReconnectTimeout]);

  const reconnect = useCallback(() => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, filter, type, connect, disconnect]);

  return {
    isConnected,
    lastEvent,
    reconnect,
    disconnect,
  };
}
